import { FormErrors } from "../types/authTypes";

// ----------------- EMAIL -----------------
export const validateEmail = (email: string): boolean => {
  if (email.trim() === "") return false;
  if (!email.includes("@")) return false;

  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z]+(\.[a-zA-Z]{2,})+$/;
  const validDomains = [".com", ".in", ".co", ".net", ".org", ".co.in", ".shine.com", ".me"];
  const hasValidDomain = validDomains.some((domain) => email.endsWith(domain));
  const isPatternValid = emailPattern.test(email);

  const domainPart = email.split("@")[1];
  if (!domainPart) return false;

  const isNameValid = /^[a-zA-Z]+$/.test(domainPart.split(".")[0]);
  return hasValidDomain && isPatternValid && isNameValid;
};

// ----------------- DATE -----------------
export const isValidDate = (day: number, month: number, year: number): boolean => {
  if (month < 1 || month > 12) return false;

  const daysInMonth = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day >= 1 && day <= daysInMonth[month - 1];
};

export const isLeapYear = (year: number): boolean => {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
};

export const formatDate = (dateString: string): string => {
  const parsedDate = parseDate(dateString);
  if (parsedDate) {
    const day = String(parsedDate.getDate()).padStart(2, "0");
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const year = parsedDate.getFullYear();
    return `${day}-${month}-${year}`;
  }
  return "";
};

export const parseDate = (dateString: string): Date | null => {
  const numericDateRegex = /^(0?[1-9]|[12][0-9]|3[01])-(0?[1-9]|1[0-2])-(\d{4})$/;
  const numericMatch = dateString.match(numericDateRegex);

  if (numericMatch) {
    const [dayStr, monthStr, yearStr] = dateString.split("-");
    const month = parseInt(monthStr, 10);
    const day = parseInt(dayStr, 10);
    const year = parseInt(yearStr, 10);

    if (yearStr.length !== 4 || isNaN(year)) return null;
    if (month < 1 || month > 12) return null;
    if (day < 1 || day > 31 || !isValidDayForMonth(day, month, year)) return null;

    return new Date(year, month - 1, day);
  }
  return null;
};

const isValidDayForMonth = (day: number, month: number, year: number): boolean => {
  const daysInMonth = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day >= 1 && day <= daysInMonth[month - 1];
};

// ----------------- DOB -----------------
export const validateDob = (rawValue: string): {
  formattedValue: string;
  date: string;
  errors: FormErrors;
} => {
  let newValue = rawValue.replace(/[^0-9-]/g, "");
  const parts = newValue.split("-");
  const errors: FormErrors = {};
  let validDate = true;

  // Validate day
  if (parts[0]) {
    const day = parseInt(parts[0], 10);
    if (day > 31) {
      parts[0] = "31";
      errors.day = "Day cannot be more than 31";
      validDate = false;
    }
  }

  // Validate month
  if (parts[1]) {
    const month = parseInt(parts[1], 10);
    if (month > 12) {
      parts[1] = "12";
      errors.month = "Month cannot be more than 12";
      validDate = false;
    }
  }

  // Validate year
  if (parts[2] && parts[2].length > 4) {
    parts[2] = parts[2].slice(0, 4);
  }

  newValue = parts.join("-");

  // Full date validation
  let date = "";
  if (parts.length === 3) {
    const [day, month, year] = parts.map((p) => parseInt(p, 10));

    if (validDate && !isValidDate(day, month, year)) {
      errors.date_of_birth = "Invalid date";
      validDate = false;
    }

    if (validDate) {
      date = formatDate(newValue) || "";
    }
  }

  return { formattedValue: newValue, date, errors };
};

// ----------------- PHONE -----------------
export const validatePhone = (rawValue: string): {
  numericValue: string;
  errors: FormErrors;
} => {
  const numericValue = rawValue.replace(/\D/g, "").slice(0, 8);
  const errors: FormErrors = {};

  if (numericValue.length !== 8) {
    errors.phone_number = "Your phone number is incomplete";
  }

  return { numericValue, errors };
};
